import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CV_BUILDER_STEPS } from '../constants';
import { useCvBuilderData } from '../data/CvBuilderDataContext';
import { getStepBySlug } from '../stepMeta';
import { CertificatesStep } from '../steps/Certificates';
import { EducationStep } from '../steps/Education';
import { HardSkillsStep } from '../steps/HardSkills';
import { LanguageStep } from '../steps/Language';
import { LanguageSkillsStep } from '../steps/LanguageSkills';
import { PersonalDetailsStep } from '../steps/PersonalDetails';
import { ProfessionalExperienceStep } from '../steps/ProfessionalExperience';
import { SoftSkillsStep } from '../steps/SoftSkills/SoftSkillsStep';
import { Overview } from './Overview';
import { StepHeader } from './StepHeader';
import { RootBox } from './styledComponents';
import { Summary } from './Summary';

const getStepComponent = (step: string) => {
  const stepComponents = {
    [CV_BUILDER_STEPS.LANGUAGE]: LanguageStep,
    [CV_BUILDER_STEPS.OVERVIEW]: Overview,
    [CV_BUILDER_STEPS.PERSONAL_DETAILS]: PersonalDetailsStep,
    [CV_BUILDER_STEPS.EDUCATION]: EducationStep,
    [CV_BUILDER_STEPS.PROFESSIONAL_EXPERIENCE]: ProfessionalExperienceStep,
    [CV_BUILDER_STEPS.HARD_SKILLS]: HardSkillsStep,
    [CV_BUILDER_STEPS.SOFT_SKILLS]: SoftSkillsStep,
    [CV_BUILDER_STEPS.LANGUAGE_SKILLS]: LanguageSkillsStep,
    [CV_BUILDER_STEPS.CERTIFICATES]: CertificatesStep,
    [CV_BUILDER_STEPS.SUMMARY]: Summary
  };

  return stepComponents[step as keyof typeof stepComponents] || LanguageStep;
};

export const StepContentSwitch = () => {
  const [searchParams] = useSearchParams();
  const { hasExistingCv } = useCvBuilderData();
  // No explicit step requested → pick the landing step by CV state: an existing CV
  // opens on the Summary (view + per-section edit), a fresh start on the first step.
  const defaultStep = hasExistingCv
    ? CV_BUILDER_STEPS.SUMMARY
    : CV_BUILDER_STEPS.LANGUAGE;
  const step = searchParams.get('step') ?? defaultStep;
  const StepComponent = getStepComponent(step);
  const stepMeta = getStepBySlug(step);

  // Steps swap via the `?step=` param (same pathname), so the page doesn't remount and
  // the scroll position carries over. Reset to the top whenever the step changes.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [step]);

  return (
    <>
      {/* Orientation lives here rather than in a page header because the guest page mounts
          no header at all — this is the one place both entry points share. StepHeader
          renders nothing for the navigation-only steps. */}
      {stepMeta && (
        <RootBox>
          <StepHeader step={stepMeta} />
        </RootBox>
      )}
      <StepComponent />
    </>
  );
};
